import { PaginatedListResult, usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { EnrolledUserItem } from "../../../UsersAndGroups/types";

export interface EditionEnrollableUser extends EnrolledUserItem {
  userExtId?: string;
  alreadyEnrolled?: boolean;
  participationStatus?: string | null;
  batchStatus?: "IN_PROGRESS" | "COMPLETE" | "ERROR" | null;
  editionName?: string;
  editionCode?: string;
}

interface UseEditionEnrollableUsersArgs {
  corporateId: string | number;
  objectId: number;
  editionId: number;
  page: number;
  pageSize: number;
  fieldFilters?: Record<string, any>;
}

const ENROLL_TYPES = "TOP,TOP_MANDATORY";

export const useEditionEnrollableUsers = ({
  corporateId,
  objectId,
  editionId,
  page,
  pageSize,
  fieldFilters,
}: UseEditionEnrollableUsersArgs): PaginatedListResult<EditionEnrollableUser> => {
  const url =
    corporateId && objectId && editionId
      ? `${ServicesURL.getEnrollment}?corporateId=${corporateId}&objectId=${objectId}&enrollType=${ENROLL_TYPES}&editionToEnroll=${editionId}`
      : null;

  return usePaginatedList<EditionEnrollableUser>(
    url,
    { page, pageSize, fieldFilters },
    {
      responseKey: "enrollmentResponses",
      totalElementsKey: "totalElements",
      totalPagesKey: "totalPages",
    },
  );
};

export const getEnrollableUserId = (u: EditionEnrollableUser): string =>
  u.userId != null && u.userId !== "" ? String(u.userId) : "";

export const getEnrollableUserName = (u: EditionEnrollableUser): string => {
  const full = [u.userFirstName, u.userLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || u.userEmail || "-";
};

export type EnrollableUserCase =
  | "enrolled-current"
  | "completed-no-edition"
  | "enrolled-elsewhere"
  | "enrollment-in-progress"
  | "enrollment-error"
  | "absent-elsewhere"
  | "enrollable";

export const classifyEnrollableUser = (
  u: EditionEnrollableUser,
): EnrollableUserCase => {
  const isAbsent = u.participationStatus === "ABSENT";
  const isCompleted = u.status === "C";
  if (u.alreadyEnrolled) return "enrolled-current";
  if (u.batchStatus === "IN_PROGRESS") return "enrollment-in-progress";
  if (u.batchStatus === "ERROR" && !isAbsent && !isCompleted)
    return "enrollment-error";
  if (u.editionId != null && isAbsent) return "absent-elsewhere";
  if (u.editionId != null) return "enrolled-elsewhere";
  if (isCompleted) return "completed-no-edition";
  return "enrollable";
};

export const isEnrollableCase = (c: EnrollableUserCase): boolean =>
  c === "enrollable" || c === "absent-elsewhere";
